<template>
  <b-modal
    v-model="modal"
    :title="title"
    size="xl"
    :no-enforce-focus="true"
    :no-close-on-backdrop="true"
  >
    <loading
      :active.sync="loading"
      color="#556ee6"
      loader="dots"
      :can-cancel="false"
      :is-full-page="false">
    </loading>
    <template #modal-header="{ close }">
      <div class="col-md-7">
        <div class="row">
          <div class="col-md-2 text-center ml-0" v-if="participant.confirmation_code">
            <a :href="`https://xcode-eventfy.sfo3.digitaloceanspaces.com/${eventName}/qrcodes/${participant.confirmation_code}.png`" target="_blank" class="link">
              <img style="width:70px;" :src="`https://xcode-eventfy.sfo3.digitaloceanspaces.com/${eventName}/qrcodes/${participant.confirmation_code}.png`"/>
            </a>
            <br />
            <span class="font-size-11">{{ participant.confirmation_code }}</span>
          </div>
          <div class="col-md-10" style="margin-left:-5px">
            <h5 class="font-size-15 text-truncate">{{ participant.name }}</h5>
            <span v-if="participant.ref" class="badge badge-pill font-size-13 badge-soft-primary">
              <i class="bx bxs-contact"></i> {{ participant.ref.data.name }}
            </span>
            <span v-if="participant.ref_id && participant.inherited_group" class="badge badge-pill font-size-13 badge-soft-secondary ml-1" title="Grupo herdado do participante principal">
              <i class="bx bx-link-alt"></i> {{ participant.inherited_group.name }}
            </span>
            <span
              v-if="participant.accommodation"
              class="badge badge-pill font-size-13 ml-1"
              :class="participant.accommodation.allocated ? 'badge-soft-success' : 'badge-soft-warning'"
              :title="participant.accommodation.label || 'Sem alocação de hospedagem'"
              style="cursor:pointer"
              @click="$router.push({ path: '/roomlist-dash', query: { participant: participant.id } })"
            >
              <i class="bx bx-bed"></i> {{ participant.accommodation.allocated ? 'Alocado' : 'Não alocado' }}
            </span>
            <p class="text-muted mb-0">Inscrito em {{ $moment(participant.created_at).format('D [de] MMMM YYYY [às] HH:mm') }}</p>
            <div class="flex items-baseline gap-1">
              <b-dropdown
                :disabled="participant.ref_id ? true : false"
                size="sm"
                variant="secondary"
                class="m-0 rounded-drop"
              >
                <template v-slot:button-content >
                  {{ participant.group ? participant.group.data.name : 'SEM GRUPO' }}
                  <i class="mdi mdi-chevron-down"></i>
                </template>
                <span v-if="isUserAdmin || isUserMaster">
                  <b-dropdown-item-button
                    v-for="(t,i) in groups"
                    :key="i"
                    @click="toggleGroup(participant.id, t.id, t.name, participant.group ? participant.group.data.name : 'SEM GRUPO')"
                  >
                    {{ t.name }}
                  </b-dropdown-item-button>
                </span>
              </b-dropdown>
              <div>
                <a :href="`https://wa.me/55${formatPhone(participant.phone)}`" target="_blank" title="Abrir conversa WhatsApp">
                  <img src="@/assets/images/whatsapp.png" height="24px"/>
                </a>
              </div>
              <div v-if="participant.facialId">
                <a :href="`https://xcode-eventfy.sfo3.digitaloceanspaces.com/${participant.selfie_path}`" target="_blank">
                  <img src="@/assets/images/facial-recognition.png" height="24px"/>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <b-dropdown
        size="sm"
        :variant="statusClass[participant.status]"
        class="m-0 rounded-drop">
        <template v-slot:button-content >
          {{ statusList[participant.status] }}
          <i class="mdi mdi-chevron-down"></i>
        </template>
        <span v-if="isUserAdmin || isUserMaster">
          <b-dropdown-item-button
            v-for="(s,idx) in statusList"
            :key="idx"
            @click="toggleStatus(participant.id, idx)"
          >
            {{ s }}
          </b-dropdown-item-button>
        </span>
      </b-dropdown>
    </template>
    <div class="row">
      <div class="col-lg-12">
        <b-tabs justified nav-class="nav-tabs-custom" content-class="p-3 text-muted" lazy>
          <b-tab active>
            <template v-slot:title>
              <span class="d-inline-block d-sm-none">
                <i class="fas fa-home"></i>
              </span>
              <span class="d-none d-sm-inline-block">Detalhes</span>
            </template>

            <!-- ── Banner IA ── -->
            <div v-if="aiAnalysisBanner" class="ai-banner ai-banner--clickable" :class="aiAnalysisBanner.type" @click="showAiSummaryModal = true">
              <div class="ai-banner-icon">
                <i class="bx bxs-magic-wand" style="font-size: 18px; color: #fff;"></i>
              </div>
              <div class="ai-banner-content">
                <span class="ai-banner-title">{{ aiAnalysisBanner.title }}</span>
                <span
                  v-for="(alert, idx) in aiAnalysisBanner.alerts"
                  :key="'alert-' + idx"
                  class="ai-banner-alert"
                  :class="alert.severity === 'critical' ? 'ai-banner-alert--critical' : 'ai-banner-alert--warning'"
                >
                  <i :class="alert.severity === 'critical' ? 'bx bx-error-circle' : 'bx bx-error'"></i>
                  {{ alert.message }}
                </span>
                <span class="ai-banner-subtitle">{{ aiAnalysisBanner.subtitle }}</span>
              </div>
              <div class="ai-banner-badge">IA</div>
            </div>
            <ai-analyses-summary-modal
              v-model="showAiSummaryModal"
              :participantId="idParticipant"
              @dismissed="reloadParticipant"
            />

            <div role="tablist">
              <!-- CAMPOS DA FICHA - CARREGADOS DO PARTICIPANT CARD BUILDER -->
              <template v-if="visibleCardTabs.length > 0">
                <b-card
                  v-for="(tab, tabIndex) in visibleCardTabs"
                  :key="`card-tab-${tab.id}`"
                  no-body
                  class="mb-1 shadow-none"
                >
                  <b-card-header header-tag="header" role="tab">
                    <h6 class="m-0 d-flex justify-content-between align-items-center">
                      <a
                        :href="`#collapse-card-tab-${tab.id}`"
                        v-b-toggle="`accordion-card-tab-${tab.id}`"
                        class="text-dark"
                      >
                        {{ tab.name }}
                        <i v-if="cardTabHasAiFields(tab)" class="bx bxs-magic-wand text-success ml-1 font-size-12" title="Contém campos verificados via IA"></i>
                      </a>
                      <div class="d-flex align-items-center">
                        <span
                          v-if="tabHasErrors(tab)"
                          class="badge bg-danger text-light mr-2"
                          :title="`${countInvalidFieldsInTab(tab)} campo(s) obrigatório(s) não preenchido(s)`"
                        >
                          {{ countInvalidFieldsInTab(tab) }} campo(s) obrigatório(s)
                        </span>
                        <i
                          class="bx bx-chevron-down chevron-icon"
                          v-b-toggle="`accordion-card-tab-${tab.id}`"
                        ></i>
                      </div>
                    </h6>
                  </b-card-header>
                  <b-collapse
                    :id="`accordion-card-tab-${tab.id}`"
                    :visible="tabIndex === 0 || tab.is_expanded_default"
                    accordion="card-accordion"
                    role="tabpanel"
                  >
                    <b-card-body>
                      <form autocomplete="off">
                        <div class="row">
                          <template v-for="field in tab.fields">
                            <div
                              v-if="isFieldVisible(field)"
                              :key="`card-field-${field.id}`"
                              :class="[`col-sm-${getCardFieldSize(field)}`, { 'ai-validated': isAiValidated(getCardFieldKey(field)), 'ai-mismatch': hasAiMismatch(getCardFieldKey(field)) }]"
                              :title="getAiFieldTooltip(getCardFieldKey(field))"
                            >
                              <component
                                :is="getCardFieldComponent(field)"
                                :id="field.id"
                                :value="customDataValues[getCardFieldKey(field)] || ''"
                                :label="getCardFieldLabel(field)"
                                :placeholder="field.placeholder || ''"
                                :options="getCardFieldOptions(field)"
                                classInput="col-sm-12"
                                :isRequired="field.is_required"
                                :isDisabled="field.field_source === 'registration_control'"
                                :isReadonly="field.is_readonly || field.field_source === 'registration_control'"
                                :forceShowError="fieldErrors[getCardFieldKey(field)]"
                                @input="updateCustomDataValue(getCardFieldKey(field), $event)"
                              />
                            </div>
                          </template>
                        </div>
                      </form>
                    </b-card-body>
                  </b-collapse>
                </b-card>
              </template>

              <!-- FALLBACK: CAMPOS DO FORMULÁRIO ANTIGO (FormBuilder) -->
              <template v-else-if="formSchema && formSchema.steps && formSchema.steps.length > 0">
                <b-card
                  v-for="(step, stepIndex) in formSchema.steps"
                  :key="`step-${step.id}`"
                  no-body
                  class="mb-1 shadow-none"
                >
                  <b-card-header header-tag="header" role="tab">
                    <h6 class="m-0 d-flex justify-content-between align-items-center">
                      <a
                        :href="`#collapse-step-${step.id}`"
                        v-b-toggle="`accordion-step-${step.id}`"
                        class="text-dark"
                      >{{ step.name }}
                        <i v-if="stepHasAiFields(step)" class="bx bxs-magic-wand text-success ml-1 font-size-12" title="Contém campos verificados via IA"></i>
                      </a>
                      <i
                        class="bx bx-chevron-down chevron-icon"
                        v-b-toggle="`accordion-step-${step.id}`"
                      ></i>
                    </h6>
                  </b-card-header>
                  <b-collapse
                    :id="`accordion-step-${step.id}`"
                    :visible="stepIndex === 0"
                    accordion="my-accordion"
                    role="tabpanel"
                  >
                    <b-card-body>
                      <form autocomplete="off">
                        <div class="row">
                          <div
                            v-for="field in step.fields"
                            :key="`field-${field.id}`"
                            :class="[`col-sm-${getFieldSize(field)}`, { 'ai-validated': isAiValidated(field.name), 'ai-mismatch': hasAiMismatch(field.name) }]"
                            :title="getAiFieldTooltip(field.name)"
                          >
                            <component
                              :is="mapComponentName(field.field_type && field.field_type.component ? field.field_type.component : 'TextField')"
                              :id="field.id"
                              :value="customDataValues[field.name] || ''"
                              :label="field.label"
                              :placeholder="field.placeholder"
                              :options="field.options || []"
                              classInput="col-sm-12"
                              :isRequired="field.required"
                              :isDisabled="field.disabled"
                              :isReadonly="field.readonly"
                              @input="updateCustomDataValue(field.name, $event)"
                            />
                          </div>
                        </div>
                      </form>
                    </b-card-body>
                  </b-collapse>
                </b-card>
              </template>
              <!-- aereos -->
              <!-- Trilha presencial -->
              <b-card no-body class="mb-1 shadow-none" v-if="journeyLabelActive">
                <b-card-header header-tag="header" role="tab">
                  <h6 class="m-0 d-flex justify-content-between align-items-center">
                    <a
                      v-b-toggle.accordion-journey
                      class="text-dark"
                      href="javascript: void(0);"
                    >Trilha presencial
                      <span v-if="journeyMarkSelected.length" class="badge bg-info text-light ml-2">{{ journeyMarkSelected.length }} marca(s)</span>
                    </a>
                    <i class="bx bx-chevron-down chevron-icon" v-b-toggle.accordion-journey></i>
                  </h6>
                </b-card-header>
                <b-collapse id="accordion-journey" accordion="my-accordion" role="tabpanel">
                  <b-card-body>
                    <div class="form-group mb-2">
                      <label>Marcas da trilha deste participante</label>
                      <multiselect
                        v-model="journeyMarkSelected"
                        :options="journeyMarksOptions"
                        :multiple="true"
                        :close-on-select="false"
                        track-by="id"
                        label="name"
                        placeholder="Selecione as marcas"
                        selectLabel=""
                        deselectLabel=""
                        selectedLabel=""
                      >
                        <template slot="option" slot-scope="props">
                          <span>{{ props.option.code ? props.option.code + ' — ' : '' }}{{ props.option.name }}</span>
                        </template>
                      </multiselect>
                      <small class="text-muted">Essas marcas saem na 2ª etiqueta impressa no credenciamento.</small>
                    </div>
                    <div class="text-right">
                      <button type="button" class="btn btn-sm btn-primary" :disabled="journeySaving" @click="saveJourneyMarks">
                        {{ journeySaving ? 'Salvando...' : 'Salvar marcas' }}
                      </button>
                    </div>
                  </b-card-body>
                </b-collapse>
              </b-card>

              <b-card no-body class="mb-1 shadow-none" v-if="displayAereo">
                <b-card-header header-tag="header" role="tab">
                  <h6 class="m-0 d-flex justify-content-between align-items-center">
                    <a
                      v-b-toggle.accordion-2
                      class="text-dark"
                      href="javascript: void(0);"
                    >Logística de transporte
                      <i v-if="isAiValidated('flights')" class="bx bxs-magic-wand text-success ml-1 font-size-12" title="Dados aplicados via IA"></i>
                      <span v-if="parseInt(participant.air_transport) === 1" class="badge bg-info text-light ml-2">Aéreo · {{ idaTransports.length + voltaTransports.length }} voo(s)</span>
                      <span v-else-if="parseInt(participant.air_transport) === 0 && displayAereo" class="badge bg-secondary text-light ml-2">Terrestre</span>
                    </a>
                    <div class="d-flex align-items-center">
                      <b-badge
                        v-if="(participant.air_transport > 0) && $v.transport.$error"
                        variant="danger"
                        class="mr-2"
                      >
                        Atenção! Existem campos a serem preenchidos.
                      </b-badge>
                      <i
                        class="bx bx-chevron-down chevron-icon"
                        v-b-toggle.accordion-2
                      ></i>
                    </div>
                  </h6>
                </b-card-header>
                <b-collapse id="accordion-2" accordion="my-accordion" role="tabpanel">
                  <b-card-body :class="{ 'ai-validated': isAiValidated('flights'), 'ai-mismatch': hasAiMismatch('flights') }">
                    <div class="row">
                      <div class="col-sm-12">
                        <div class="form-group">
                          <label for="air_transport">Meio de transporte</label>
                            <select
                              class="custom-select"
                              name="air_transport"
                              id="air_transport"
                              v-model="participant.air_transport"
                            >
                              <option value="1">Aéreo</option>
                              <option value="0">Terrestre</option>
                            </select>
                          </div>
                      </div>
                    </div>
                    <div v-if="parseInt(participant.air_transport) === 1" :key="transportKey">
                      <div class="row mb-2">
                        <div class="col-sm-12 text-right">
                          <button type="button" class="btn btn-sm btn-outline-primary" @click="flightBlockModal = true">
                            <i class="mdi mdi-airplane mr-1"></i> Aplicar bloqueio
                          </button>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-sm-12">
                          <div class="d-flex justify-content-between">
                            <p class="card-title mt-2">
                              <span class="btn btn-sm btn-success mr-2 mb-1">
                                <i class="mdi-airplane-takeoff mdi"></i>
                              </span> Voos de IDA
                            </p>
                            <p class="mt-2 link text-primary" @click="addVoo('IDA')">+ Adicionar voo de IDA</p>
                          </div>
                        </div>
                      </div>
                      <span
                        style="background: #fdfdfd; padding: 10px; border-radius: 5px; border: 1px solid #f1f1f1; display: block; margin-bottom: 10px;"
                        v-for="(transport, idx) in idaTransports" :key="idx"
                      >
                        <div class="row">
                          <div class="col-sm-12">
                            <div class="d-flex justify-content-between align-items-center">
                              <span v-if="transport.flight_block_id" class="badge bg-info text-light">
                                <i class="mdi mdi-link-variant"></i> Bloqueio #{{ transport.flight_block_id }}
                              </span>
                              <span v-else></span>
                              <span class="mt-2 link text-danger" @click="removeVoo('IDA', idx)"><i class="bx bx-trash"></i></span>
                            </div>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col-sm-4">
                            <div class="form-group">
                              <label for="airline">Companhia Aérea</label>
                              <input
                                class="form-control"
                                type="text"
                                name="airline"
                                id="airline"
                                v-model="transport.airline"
                              >
                            </div>
                          </div>
                          <div class="col-sm-4">
                            <div class="form-group">
                              <label for="origin">Origem</label>
                              <input
                                id="origin"
                                v-model="transport.origin"
                                name="origin"
                                type="text"
                                class="form-control"
                              />
                            </div>
                          </div>
                          <div class="col-sm-4">
                            <div class="form-group">
                              <label for="destination">Destino</label>
                              <input
                                id="destination"
                                v-model="transport.destination"
                                name="destination"
                                type="text"
                                class="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col-sm-3">
                            <div class="form-group">
                              <label for="locator">Localizador IDA</label>
                              <input
                                id="locator"
                                v-model="transport.locator"
                                name="locator"
                                type="text"
                                class="form-control"
                              />
                            </div>
                          </div>
                          <div class="col-sm-3">
                            <div class="form-group">
                              <label for="eticket_ida">E-ticket IDA</label>
                              <input
                                id="eticket_ida"
                                v-model="transport.eticket"
                                name="eticket"
                                type="text"
                                class="form-control"
                              />
                            </div>
                          </div>
                          <div class="col-sm-3">
                            <div class="form-group">
                              <label for="flight_number">Número do Voo</label>
                              <input
                                id="flight_number"
                                v-model="transport.flight_number"
                                name="flight_number"
                                type="text"
                                class="form-control"
                              />
                            </div>
                          </div>
                          <div class="col-sm-3">
                            <div class="form-group">
                              <label for="boarding_date">Data de Embarque</label>
                              <date-picker
                                type="datetime"
                                v-model="transport.boarding_date"
                                :first-day-of-week="1"
                                lang="pt-br"
                                format="DD/MM/YYYY HH:mm"
                              >
                              </date-picker>
                            </div>
                          </div>
                          <div class="col-sm-3">
                            <div class="form-group">
                              <label for="landing_date">Data de Desembarque</label>
                              <date-picker
                                type="datetime"
                                v-model="transport.landing_date"
                                :first-day-of-week="1"
                                lang="pt-br"
                                format="DD/MM/YYYY HH:mm"
                              >
                              </date-picker>
                            </div>
                          </div>
                        </div>
                      </span>
                      <hr class="mt-2"/>
                      <div class="row">
                        <div class="col-sm-12">
                          <div class="d-flex justify-content-between">
                            <p class="card-title mt-2">
                              <span class="btn btn-sm btn-warning mr-2 mb-1">
                                <i class="mdi-airplane-landing mdi"></i>
                              </span> Voos de VOLTA
                            </p>
                            <p class="mt-2 link text-primary" @click="addVoo('VOLTA')">+ Adicionar voo de VOLTA</p>
                          </div>
                        </div>
                      </div>
                      <span
                        style="background: #fdfdfd; padding: 10px; border-radius: 5px; border: 1px solid #f1f1f1; display: block; margin-bottom: 10px;"
                        v-for="(transport, idx) in voltaTransports" :key="idx+100"
                      >
                        <div class="row">
                          <div class="col-sm-12">
                            <div class="d-flex justify-content-between align-items-center">
                              <span v-if="transport.flight_block_id" class="badge bg-warning text-light">
                                <i class="mdi mdi-link-variant"></i> Bloqueio #{{ transport.flight_block_id }}
                              </span>
                              <span v-else></span>
                              <span class="mt-2 link text-danger" @click="removeVoo('VOLTA', idx)"><i class="bx bx-trash"></i></span>
                            </div>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col-sm-4">
                            <div class="form-group">
                              <label for="airline">Companhia Aérea</label>
                              <input
                                class="form-control"
                                type="text"
                                name="airline"
                                id="airline"
                                v-model="transport.airline"
                              >
                            </div>
                          </div>
                          <div class="col-sm-4">
                            <div class="form-group">
                              <label for="origin">Origem</label>
                              <input
                                id="origin"
                                v-model="transport.origin"
                                name="origin"
                                type="text"
                                class="form-control"
                              />
                            </div>
                          </div>
                          <div class="col-sm-4">
                            <div class="form-group">
                              <label for="destination">Destino</label>
                              <input
                                id="destination"
                                v-model="transport.destination"
                                name="destination"
                                type="text"
                                class="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col-sm-3">
                            <div class="form-group">
                              <label for="locator">Localizador VOLTA</label>
                              <input
                                id="locator"
                                v-model="transport.locator"
                                name="locator"
                                type="text"
                                class="form-control"
                              />
                            </div>
                          </div>
                          <div class="col-sm-3">
                            <div class="form-group">
                              <label for="eticket_volta">E-ticket VOLTA</label>
                              <input
                                id="eticket_volta"
                                v-model="transport.eticket"
                                name="eticket"
                                type="text"
                                class="form-control"
                              />
                            </div>
                          </div>
                          <div class="col-sm-3">
                            <div class="form-group">
                              <label for="flight_number">Número do Voo</label>
                              <input
                                id="flight_number"
                                v-model="transport.flight_number"
                                name="flight_number"
                                type="text"
                                class="form-control"
                              />
                            </div>
                          </div>
                          <div class="col-sm-3">
                            <div class="form-group">
                              <label for="boarding_date">Data de Embarque</label>
                              <date-picker
                                type="datetime"
                                v-model="transport.boarding_date"
                                :first-day-of-week="1"
                                lang="pt-br"
                                format="DD/MM/YYYY HH:mm"
                              >
                              </date-picker>
                            </div>
                          </div>
                          <div class="col-sm-3">
                            <div class="form-group">
                              <label for="landing_date">Data de Desembarque</label>
                              <date-picker
                                type="datetime"
                                v-model="transport.landing_date"
                                :first-day-of-week="1"
                                lang="pt-br"
                                format="DD/MM/YYYY HH:mm"
                              >
                              </date-picker>
                            </div>
                          </div>
                        </div>
                      </span>
                    </div>
                  </b-card-body>
                </b-collapse>
              </b-card>
              <!-- Controle de Inscrição -->
              <b-card no-body class="mb-1 shadow-none" v-if="displayRegistrationControl">
                <b-card-header header-tag="header" role="tab">
                  <h6 class="m-0 d-flex justify-content-between align-items-center">
                    <a
                      v-b-toggle.accordion-reg-control
                      class="text-dark"
                      href="javascript: void(0);"
                    >Controle de Inscrição</a>
                    <i
                      class="bx bx-chevron-down chevron-icon"
                      v-b-toggle.accordion-reg-control
                    ></i>
                  </h6>
                </b-card-header>
                <b-collapse id="accordion-reg-control" accordion="card-accordion" role="tabpanel">
                  <b-card-body>
                    <form autocomplete="off">
                      <div class="row">
                        <div class="col-sm-6">
                          <div class="form-group">
                            <label>{{ registrationControlItemData.identifier_label || 'Identificador' }}</label>
                            <input
                              type="text"
                              class="form-control"
                              :value="registrationControlItemData.identifier"
                              readonly
                            />
                          </div>
                        </div>
                        <div class="col-sm-6">
                          <div class="form-group">
                            <label>Nome</label>
                            <input
                              type="text"
                              class="form-control"
                              :value="registrationControlItemData.name"
                              readonly
                            />
                          </div>
                        </div>
                        <div class="col-sm-6" v-if="registrationControlItemData.control_name">
                          <div class="form-group">
                            <label>Controle</label>
                            <input
                              type="text"
                              class="form-control"
                              :value="registrationControlItemData.control_name"
                              readonly
                            />
                          </div>
                        </div>
                        <div class="col-sm-6" v-if="registrationControlItemData.group_name">
                          <div class="form-group">
                            <label>Grupo</label>
                            <input
                              type="text"
                              class="form-control"
                              :value="registrationControlItemData.group_name"
                              readonly
                            />
                          </div>
                        </div>
                        <div class="col-sm-12" v-if="registrationControlItemData.tag_bundle_name">
                          <div class="form-group">
                            <label>Grupo de Tags</label>
                            <input
                              type="text"
                              class="form-control"
                              :value="registrationControlItemData.tag_bundle_name"
                              readonly
                            />
                          </div>
                        </div>
                        <template v-if="registrationControlItemData.fields_schema && registrationControlItemData.fields_schema.length">
                          <div
                            class="col-sm-6"
                            v-for="schemaField in registrationControlItemData.fields_schema"
                            :key="schemaField.name"
                          >
                            <div class="form-group">
                              <label>{{ schemaField.label || schemaField.name }}</label>
                              <input
                                type="text"
                                class="form-control"
                                :value="(registrationControlItemData.data && registrationControlItemData.data[schemaField.name]) || ''"
                                readonly
                              />
                            </div>
                          </div>
                        </template>
                        <template v-else-if="registrationControlItemData.data">
                          <div class="col-sm-6" v-for="(value, key) in registrationControlItemData.data" :key="key">
                            <div class="form-group">
                              <label>{{ getRegistrationControlFieldLabel(key) }}</label>
                              <input
                                type="text"
                                class="form-control"
                                :value="value"
                                readonly
                              />
                            </div>
                          </div>
                        </template>
                      </div>
                    </form>
                  </b-card-body>
                </b-collapse>
              </b-card>

              <b-card no-body class="mb-1 shadow-none" v-if="!participant.ref_id && displayCompanion">
                <b-card-header header-tag="header" role="tab">
                  <h6 class="m-0 d-flex justify-content-between align-items-center">
                    <a
                      v-b-toggle.accordion-3
                      class="text-dark"
                      href="javascript: void(0);"
                    >Acompanhante <span class="badge badge bg-dark text-light ml-2 badge-count">{{ participant.companion ? participant.companion.data.length : '0' }}</span></a>
                    <i
                      class="bx bx-chevron-down chevron-icon"
                      v-b-toggle.accordion-3
                    ></i>
                  </h6>
                </b-card-header>
                <b-collapse id="accordion-3" accordion="my-accordion" role="tabpanel">
                  <b-card-body>
                    <div
                      v-if="!participant.has_companion || participant.companion.data.length < maxCompanions"
                      role="alert"
                      class="alert alert-dismissible alert-info"
                    >
                      <i class="mdi mdi-alert-circle-outline me-2"></i> Deseja adicionar acompanhante ?
                      <a href="javascript: void(0);" @click="newCompanion(participant.id)">
                        [Clique aqui]
                      </a>
                      <span class="mx-2">·</span>
                      <a href="javascript: void(0);" @click="openLinkCompanion()">
                        [Vincular existente]
                      </a>
                    </div>
                    <div class="row" v-if="participant.has_companion">
                      <div class="col-md-6" v-for="(c, i) in companion" :key="i">
                        <div class="card mini-stats-wid border-gray">
                          <div class="card-body">
                            <div class="d-flex justify-content-between align-items-start">
                              <div class="media flex-grow-1 link" @click="openCompanion(c.id)">
                                <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-primary">
                                  <span class="avatar-title">
                                    <i class="bx bx-group font-size-24"></i>
                                  </span>
                                </div>
                                <div class="media-body ml-3">
                                  <h5 class="font-size-14 mb-1">
                                    {{ c.name }}
                                    <span v-if="c.event_companion" class="badge badge-pill font-size-12 badge-soft-primary">Evento</span>
                                    <span v-if="c.travel_companion" class="badge badge-pill font-size-12 badge-soft-danger ml-1">Viagem</span>
                                  </h5>
                                  <p class="text-muted mb-0">{{ c.email }}</p>
                                  <p class="text-muted mb-0">{{ c.phone }}</p>
                                </div>
                              </div>
                              <b-dropdown
                                v-if="isUserAdmin || isUserMaster"
                                right
                                size="sm"
                                variant="link"
                                no-caret
                                toggle-class="text-muted p-0"
                                title="Mais ações"
                              >
                                <template #button-content>
                                  <i class="mdi mdi-dots-vertical font-size-18"></i>
                                </template>
                                <b-dropdown-item @click="unlinkCompanionAction(c)">
                                  <i class="mdi mdi-link-variant-off text-danger mr-1"></i>
                                  Desvincular
                                </b-dropdown-item>
                              </b-dropdown>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </b-card-body>
                </b-collapse>
              </b-card>

              <participant-financials
                v-if="displayFinancialBlock"
                :idParticipant="idParticipant"
                :participant="participant" />

              <b-card no-body class="mb-1 shadow-none tags-card">
                <b-card-header header-tag="header" role="tab">
                  <h6 class="m-0 d-flex justify-content-between align-items-center">
                    <a
                      v-b-toggle.accordion-5
                      class="text-dark"
                      href="javascript: void(0);"
                    >Tags de Acesso <span v-if="participant.tags && participant.tags.length" class="badge badge bg-dark text-light ml-2 badge-count">{{ participant.tags.length }}</span></a>
                    <i
                      class="bx bx-chevron-down chevron-icon"
                      v-b-toggle.accordion-5
                    ></i>
                  </h6>
                </b-card-header>
                <b-collapse id="accordion-5" accordion="my-accordion" role="tabpanel">
                  <b-card-body>
                    <div class="row" v-if="isUserAdmin">
                      <div class="col-sm-12">
                        <div class="form-group">
                          <label for="tags">Tags</label>
                          <multiselect
                            tag-placeholder="Selecione uma ou mais tags"
                            placeholder="Selecione uma ou mais tags"
                            v-model="participant.tags"
                            label="name"
                            track-by="id"
                            :options="tags"
                            :multiple="true">
                          </multiselect>
                        </div>
                      </div>
                    </div>
                  </b-card-body>
                </b-collapse>
              </b-card>

            </div>

          </b-tab>
          <b-tab>
            <template v-slot:title>
              <span class="d-inline-block d-sm-none">
                <i class="far fa-user"></i>
              </span>
              <span class="d-none d-sm-inline-block">Anexos</span>
            </template>
            <attachments :attachments="attachments" :idParticipant="idParticipant" @dataApplied="reloadParticipant"></attachments>
          </b-tab>
          <b-tab>
            <template v-slot:title>
              <span class="d-inline-block d-sm-none">
                <i class="far fa-envelope"></i>
              </span>
              <span class="d-none d-sm-inline-block">Histórico</span>
            </template>
            <historics :idParticipant=idParticipant></historics>
          </b-tab>
          <b-tab>
            <template v-slot:title>
              <span class="d-inline-block d-sm-none">
                <i class="fas fa-cog"></i>
              </span>
              <span class="d-none d-sm-inline-block">Notificações</span>
            </template>
            <log-mails :idParticipant=idParticipant></log-mails>
          </b-tab>
          <b-tab v-if="idParticipant">
            <template v-slot:title>
              <span class="d-inline-block d-sm-none">
                <i class="bx bx-file"></i>
              </span>
              <span class="d-none d-sm-inline-block">Cartas Pax</span>
            </template>
            <carta-pax-sends :idParticipant="idParticipant"></carta-pax-sends>
          </b-tab>
          <b-tab v-if="participant.need_payment">
            <template v-slot:title>
              <span class="d-inline-block d-sm-none">
                <i class="fas fa-cog"></i>
              </span>
              <span class="d-none d-sm-inline-block">Pagamento</span>
            </template>
            <log-payments :idParticipant=idParticipant></log-payments>
          </b-tab>
          <b-tab v-if="idParticipant && hasReturnActions" lazy>
            <template v-slot:title>
              <span class="d-inline-block d-sm-none">
                <i class="far fa-paper-plane"></i>
              </span>
              <span class="d-none d-sm-inline-block">Ações de retorno</span>
            </template>
            <pending-actions :idParticipant=idParticipant></pending-actions>
          </b-tab>
        </b-tabs>
      </div>
    </div>
    <template v-slot:modal-footer>
      <p class="text-muted mb-0" style="flex:auto">Última atualização em {{ $moment(participant.updated_at).format('D [de] MMMM YYYY [às] HH:mm') }}</p>
      <b-button v-if="(isUserAdmin || isUserMaster) && participant.id" variant="danger" @click="deleteParticipant(participant)" class="float-left">Excluir Inscrito</b-button>
      <b-button variant="secondary" @click="close()">Cancelar</b-button>
      <button type="submit" class="btn btn-primary mr-1" @click="saveParticipant()" v-if="isUserAdmin || isUserMaster">Salvar</button>
    </template>
    <companion-form v-model="modalForm" :idParticipant="idParticipant" ref="formCompanion" @created="loadItem(idParticipant)"></companion-form>
    <link-companion ref="linkCompanion" :mainParticipantId="idParticipant" @linked="loadItem(idParticipant)"></link-companion>
    <participant-form v-model="modalParticipant" ref="formParticipant"></participant-form>
    <apply-flight-block-modal
      v-model="flightBlockModal"
      :participant-id="participant.id"
      :participant-group-id="participant.group_id"
      @applied="loadItem(participant.id)"
    ></apply-flight-block-modal>
  </b-modal>
</template>
<script>
// Import component
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import Swal from 'sweetalert2'
import { mapActions, mapState, mapGetters } from 'vuex'
import { states } from './statesData'
import DatePicker from 'vue2-datepicker'
import Moment from 'moment'
import { required, email } from 'vuelidate/lib/validators'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import ParticipantForm from './ModalForm'
import CompanionForm from './Companion'
import LinkCompanion from './LinkCompanion'
import Attachments from './Attachments'
import Historics from './Historics'
import LogMails from './LogMails'
import PendingActions from './PendingActions'
import { list as listPendingActions } from '@/pages/pending-actions/services'
import CartaPaxSends from './CartaPaxSends'
import Multiselect from 'vue-multiselect'
import LogPayments from './LogPayments'
import ApplyFlightBlockModal from './ApplyFlightBlockModal'
import ParticipantFinancials from './financials/Financials.vue'
import { cpf } from 'cpf-cnpj-validator'
import { EventBus } from '@/eventBus'
import services, { unlinkCompanion as apiUnlinkCompanion } from './services'
import Renderer from '@/components/formSchema/Renderer'
import BaseInput from '@/components/formSchema/BaseInput'
import BaseInputEmail from '@/components/formSchema/BaseInputEmail'
import BaseInputPhone from '@/components/formSchema/BaseInputPhone'
import BaseSelect from '@/components/formSchema/BaseSelect'
import BaseRadio from '@/components/formSchema/BaseRadio'
import BaseTextArea from '@/components/formSchema/BaseTextArea'
import BaseInputDate from '@/components/formSchema/BaseInputDate'
import BaseInputDateTime from '@/components/formSchema/BaseInputDateTime'
import BaseInputCPF from '@/components/formSchema/BaseInputCPF'
import BaseInputCNPJ from '@/components/formSchema/BaseInputCNPJ'
import BaseMultiselect from '@/components/formSchema/BaseMultiselect'
import BaseTermsCheckbox from '@/components/formSchema/BaseTermsCheckbox'
import AiAnalysesSummaryModal from './AiAnalysesSummaryModal'

export default {
  name: 'form-participant',
  components: {
    Loading,
    DatePicker,
    Multiselect,
    CompanionForm,
    LinkCompanion,
    ParticipantForm,
    Attachments,
    Historics,
    LogMails,
    PendingActions,
    CartaPaxSends,
    LogPayments,
    ApplyFlightBlockModal,
    ParticipantFinancials,
    Renderer,
    BaseInput,
    BaseInputEmail,
    BaseInputPhone,
    BaseSelect,
    BaseRadio,
    BaseTextArea,
    BaseInputDate,
    BaseInputDateTime,
    BaseInputCPF,
    BaseInputCNPJ,
    BaseMultiselect,
    BaseTermsCheckbox,
    AiAnalysesSummaryModal
  },
  props: {
    idParticipant: {
      type: Number || String,
      default: null
    }
  },
  data () {
    return {
      title: 'Novo Participante',
      loading: true,
      // Trilha presencial
      journeyLabelActive: false,
      journeyMarksOptions: [],
      journeyMarkSelected: [],
      journeySaving: false,
      hasReturnActions: false,
      selected: '',
      errorCPF: false,
      status: null,
      fullPage: true,
      submitted: false,
      states: states,
      attachments: [],
      listTags: [],
      participant: {
        customData: [],
        air_transport: 0,
        displayAereo: 0
      },
      formSchema: null,
      participantCard: null,
      customDataValues: {},
      fieldErrors: {}, // Rastreia erros por campo {fieldKey: true/false}
      forceValidation: false, // Flag para forçar validação de todos os campos
      hasTriedSubmit: false, // Flag para indicar que já tentou fazer submit
      formCustom: {},
      idaTransports: [],
      voltaTransports: [],
      transportKey: 0,
      flightBlockModal: false,
      contact: {},
      companion: {},
      statusClass: ['dark', 'primary', 'warning', 'success', 'danger', 'info'],
      modal: false,
      modalForm: false,
      modalParticipant: false,
      aiPollingInterval: null,
      aiHasPendingAnalysis: false,
      showAiSummaryModal: false
    }
  },
  validations () {
    const fNormal = {
      name: { required },
      badge_name: { required },
      email: { required, email },
      phone: { required },
      city: { required },
      state: { required }
    }
    if (!this.participant.air_transport || !this.displayAereo) {
      let participant = fNormal
      return {
        participant: participant
      }
    } else {
      let participant = fNormal
      return {
        participant: participant,
        transport: {}
      }
    }
  },
  methods: {
    // ===== Trilha presencial =====
    journeyParticipantId () {
      // A ficha pode ser modal (open(id)) — o prop idParticipant nem sempre está setado.
      // Usa o participante REALMENTE carregado.
      return (this.participant && this.participant.id) || this.idParticipant || null
    },
    loadJourneyConfig () {
      // config + catálogo: carrega UMA vez (promise cacheada), reusado a cada participante.
      if (!this._journeyConfigPromise) {
        this._journeyConfigPromise = (async () => {
          try {
            const cfg = (await services.journeyConfig()).data
            this.journeyLabelActive = !!(cfg && cfg.has_journey_label)
            if (this.journeyLabelActive) {
              const marks = (await services.journeyMarksList()).data
              this.journeyMarksOptions = (marks && marks.data) ? marks.data : []
            }
          } catch (e) {
            this.journeyLabelActive = false
          }
        })()
      }
      return this._journeyConfigPromise
    },
    async loadParticipantMarks () {
      await this.loadJourneyConfig()
      this.journeyMarkSelected = [] // limpa o do participante anterior (evita valor "stale")
      if (!this.journeyLabelActive) return
      const pid = this.journeyParticipantId()
      if (!pid) return
      try {
        const pm = (await services.getParticipantMarks(pid)).data
        this.journeyMarkSelected = (pm && pm.data) ? pm.data : []
      } catch (e) {
        this.journeyMarkSelected = []
      }
    },
    async saveJourneyMarks () {
      const pid = this.journeyParticipantId()
      if (!pid) {
        errorMessage('Salve o participante primeiro para atribuir as marcas.')
        return
      }
      this.journeySaving = true
      try {
        const ids = (this.journeyMarkSelected || []).map(m => m.id)
        await services.setParticipantMarks(pid, ids)
        successMessage('Marcas da trilha salvas.')
      } catch (e) {
        errorMessage((e.response && e.response.data && e.response.data.message) || 'Erro ao salvar as marcas.')
      } finally {
        this.journeySaving = false
      }
    },
    ...mapActions('settingRSVP', {
      GetRSVP: 'GetRSVP'
    }),
    ...mapActions('tag', {
      getAllTags: 'getAll'
    }),
    ...mapActions('group', {
      getAll: 'getAll'
    }),
    ...mapActions('participant', {
      getParticipant: 'getParticipant',
      postData: 'save',
      updateStatus: 'changeStatus',
      updateType: 'changeType',
      destroyParticipant: 'deleteParticipant',
      destroyVoo: 'deleteVoo'
    }),
    validCPF () {
      const cpfValid = cpf.isValid(this.participant.cpf)
      if (!cpfValid) {
        this.errorCPF = true
        return
      }
      this.errorCPF = false
    },
    formatPhone (phone) {
      if (phone) {
        return phone.replace(/\D/g, '')
      }
    },
    addVoo (type) {
      if (type === 'IDA') {
        this.idaTransports.push({ type_flight: type })
      } else {
        this.voltaTransports.push({ type_flight: type })
      }
    },
    removeVoo (type, idx) {
      if (this.isUserAdmin) {
        Swal.fire({
          title: 'Você tem certeza ?',
          html: `Será excluído do sistema voo de <b>${type} #${idx + 1}</b>`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#f46a6a',
          cancelButtonColor: '#4a4848',
          cancelButtonText: 'CANCELAR',
          confirmButtonText: 'SIM, DESEJO EXCLUIR!'
        }).then(result => {
          if (result.value) {
            try {
              if (type === 'IDA') {
                if (this.idaTransports[idx].id) {
                  this.destroyVoo(this.idaTransports[idx].id).then((res) => {
                    successMessage('Sucesso: Voo excluído com sucesso.', 2500)
                  }).catch((err) => {
                    errorMessage('Alerta: ' + err.message)
                  })
                }
                this.idaTransports.splice(idx, 1)
              } else {
                if (this.voltaTransports[idx].id) {
                  this.destroyVoo(this.voltaTransports[idx].id).then((res) => {
                    successMessage('Sucesso: Voo excluído com sucesso.', 2500)
                  }).catch((err) => {
                    errorMessage('Alerta: ' + err.message)
                  })
                }
                this.voltaTransports.splice(idx, 1)
              }
            } catch (err) {
              errorMessage('Alerta: ' + err.response.data.message)
            }
          }
        })
        return
      }
      errorMessage('Atenção: Você não tem permissão para realizar essa ação!', 2500)
    },
    reloadParticipant () {
      if (this.idParticipant) {
        this.loadItem(this.idParticipant)
        this.checkAiAnalysisPending()
      }
    },
    async checkAiAnalysisPending () {
      if (!this.idParticipant) return
      try {
        const res = await services.listAttachments(this.idParticipant)
        const attachments = res.data || []
        this.aiHasPendingAnalysis = attachments.some(
          a => a.analysis_status === 'pending' || a.analysis_status === 'processing'
        )
        if (this.aiHasPendingAnalysis) {
          this.startAiPolling()
        } else {
          this.stopAiPolling()
        }
      } catch (e) {
        // silenciar — polling não deve bloquear a UI
      }
    },
    startAiPolling () {
      if (this.aiPollingInterval) return
      this.aiPollingInterval = setInterval(async () => {
        try {
          const res = await services.listAttachments(this.idParticipant)
          const attachments = res.data || []
          const stillPending = attachments.some(
            a => a.analysis_status === 'pending' || a.analysis_status === 'processing'
          )
          if (!stillPending) {
            this.stopAiPolling()
            this.aiHasPendingAnalysis = false
            this.loadItem(this.idParticipant)
          }
        } catch (e) {
          // silenciar
        }
      }, 15000)
    },
    stopAiPolling () {
      if (this.aiPollingInterval) {
        clearInterval(this.aiPollingInterval)
        this.aiPollingInterval = null
      }
    },
    async loadItem (id) {
      if (!id) return
      // Guard: evita chamadas duplicadas simultâneas
      if (this._loadingItem) return
      this._loadingItem = true

      try {
        this.title = 'Editar Participante'
        const freshData = await this.getParticipant(id)

        if (!freshData) {
          console.warn('[Form] getParticipant retornou vazio para id:', id)
          return
        }

        this.participant = freshData
        this.participant.birth_date = freshData.birth_date || null

        if (this.participant.tags) {
          this.participant.tags = this.participant.tags.data
        }

        // Processar transports
        this.idaTransports = []
        this.voltaTransports = []
        if (freshData.transports && freshData.transports.data) {
          freshData.transports.data.forEach(transport => {
            if (transport.type_flight === 'IDA') {
              this.idaTransports.push({
                ...transport,
                boarding_date: transport.boarding_date ? Moment(transport.boarding_date, 'YYYY-MM-DD HH:mm').toDate() : null,
                landing_date: transport.landing_date ? Moment(transport.landing_date, 'YYYY-MM-DD HH:mm').toDate() : null
              })
            } else if (transport.type_flight === 'VOLTA') {
              this.voltaTransports.push({
                ...transport,
                boarding_date: transport.boarding_date ? Moment(transport.boarding_date, 'YYYY-MM-DD HH:mm').toDate() : null,
                landing_date: transport.landing_date ? Moment(transport.landing_date, 'YYYY-MM-DD HH:mm').toDate() : null
              })
            }
          })
        }
        this.transportKey++

        if (freshData.contact) {
          this.contact = freshData.contact.data
        }
        if (freshData.companion) {
          this.companion = freshData.companion.data
        }
        if (freshData.attachments) {
          this.attachments = freshData.attachments.data
        }
        // Load form schema for the participant's group (needed for field options)
        if (this.participant.group_id) {
          await this.loadFormSchema(this.participant.group_id).catch(() => {})
        }
        // Load form schema for the participant's group (needed for field options)
        if (this.participant.group_id) {
          await this.loadFormSchema(this.participant.group_id).catch(() => {})
        }
        // Load participant card configuration (sempre executa, independente do form schema)
        await this.loadParticipantCard(this.participant.id)
      } catch (err) {
        console.error('[Form] loadItem erro:', err)
      } finally {
        this._loadingItem = false
        this.loading = false
        this.checkAiAnalysisPending()
        this.checkReturnActions()
        this.loadParticipantMarks()
      }
    },
    // Mostra a aba "Ações de retorno" só se o participante tiver alguma ação.
    async checkReturnActions () {
      if (!this.idParticipant) {
        this.hasReturnActions = false
        return
      }
      try {
        const res = await listPendingActions({ participant_id: this.idParticipant, limit: 1 })
        const data = (res.data && res.data.data) ? res.data.data : []
        this.hasReturnActions = data.length > 0
      } catch (e) {
        this.hasReturnActions = false
      }
    },
    async loadFormSchema (groupId) {
      if (!groupId) {
        this.formSchema = null
        this.customDataValues = {}
        return
      }
      try {
        const response = await services.getFormByGroupId(groupId)
        if (response) {
          this.formSchema = response

          // NOVO: Carregar dados separando standard de custom
          const allFields = this.allFormFields

          allFields.forEach(field => {
            // Verifica se é campo standard
            const isStandard = field.field_type && field.field_type.is_standard && field.field_type.maps_to_column

            if (isStandard) {
              // Carrega do participant direto
              const columnName = field.field_type.maps_to_column
              const value = this.participant[columnName]
              if (value) {
                this.$set(this.customDataValues, field.name, value)
              }
            } else {
              // Carrega de custom_data
              const formDataSource = (this.data && this.data.formData)
                ? this.data.formData
                : this.participant.formData

              if (formDataSource) {
                const formDataArray = formDataSource.data || formDataSource

                if (Array.isArray(formDataArray)) {
                  const customData = formDataArray.find(cd => cd.form_field_id === field.id)
                  if (customData) {
                    this.$set(this.customDataValues, field.name, customData.data)
                  }
                }
              }
            }
          })
        } else {
          this.formSchema = null
        }
      } catch (error) {
        console.error('Error loading form schema:', error)
        this.formSchema = null
      }
    },
    async loadParticipantCard (participantId) {
      if (!participantId) {
        this.participantCard = null
        return
      }
      try {
        console.log('📥 [DEBUG] Carregando participantCard para participant ID:', participantId)
        const response = await services.getParticipantCard(participantId)
        if (response) {
          this.participantCard = response
          console.log('📥 [DEBUG] participantCard carregado:', this.participantCard)
          console.log('📥 [DEBUG] participant.formData:', this.participant.formData)
          console.log('📥 [DEBUG] data.formData:', this.data && this.data.formData)

          // Carregar valores dos campos
          if (this.participantCard.tabs) {
            this.participantCard.tabs.forEach(tab => {
              if (tab.fields) {
                tab.fields.forEach(field => {
                  console.log('📥 [DEBUG] Carregando valor do campo:', field.label || (field.field_config && field.field_config.label), '- source:', field.field_source)
                  this.loadCardFieldValue(field)
                })
              }
            })
          }
          console.log('📥 [DEBUG] customDataValues após carregamento:', this.customDataValues)
        } else {
          this.participantCard = null
        }
      } catch (error) {
        console.error('Error loading participant card:', error)
        this.participantCard = null
      }
    },
    loadCardFieldValue (field) {
      // Standard field: carrega do participant direto
      if (field.field_source === 'standard' && field.standard_field_name) {
        const columnMap = {
          name: 'name',
          badge_name: 'badge_name',
          email: 'email',
          cpf: 'cpf',
          passaporte: 'passaporte',
          phone: 'phone',
          gender: 'gender',
          birth_date: 'birth_date',
          address: 'address',
          city: 'city',
          state: 'state',
          zip_code: 'zip_code',
          country: 'country'
        }
        const columnName = columnMap[field.standard_field_name]
        if (columnName && this.participant[columnName]) {
          let loadedValue = this.participant[columnName]
          // Converter birth_date para ISO (YYYY-MM-DD) - formato nativo do input type="date"
          if (field.standard_field_name === 'birth_date' && loadedValue) {
            if (loadedValue instanceof Date && !isNaN(loadedValue.getTime())) {
              var yyyy = loadedValue.getFullYear()
              var mm = ('0' + (loadedValue.getMonth() + 1)).slice(-2)
              var dd = ('0' + loadedValue.getDate()).slice(-2)
              loadedValue = yyyy + '-' + mm + '-' + dd
            } else if (typeof loadedValue === 'string' && loadedValue.includes('/')) {
              var parts = loadedValue.split('/')
              if (parts.length === 3) {
                loadedValue = parts[2] + '-' + parts[1] + '-' + parts[0]
              }
            } else if (typeof loadedValue === 'string' && loadedValue.includes('-')) {
              loadedValue = loadedValue.split('T')[0]
            }
          }
          console.log('  ✅ Campo STANDARD carregado:', field.standard_field_name, '=', loadedValue)
          this.$set(this.customDataValues, field.standard_field_name, loadedValue)
        } else {
          // Fallback: tentar carregar do formSchema (caso o valor não esteja na tabela participants)
          if (this.allFormFields && this.allFormFields.length > 0) {
            var standardFieldName = field.standard_field_name
            var formField = this.allFormFields.find(function (f) {
              return f.field_type && f.field_type.maps_to_column === standardFieldName
            })
            if (formField && this.customDataValues[formField.name]) {
              console.log('  🔄 Campo STANDARD carregado via formSchema fallback:', standardFieldName, '=', this.customDataValues[formField.name])
              this.$set(this.customDataValues, standardFieldName, this.customDataValues[formField.name])
            } else {
              console.log('  ⚠️ Campo STANDARD vazio:', standardFieldName)
            }
          } else {
            console.log('  ⚠️ Campo STANDARD vazio:', field.standard_field_name)
          }
        }
      }

      // Form field: carrega de custom_data
      if (field.field_source === 'form_field' && field.form_field) {
        const formDataSource = (this.data && this.data.formData)
          ? this.data.formData
          : this.participant.formData

        console.log('  🔍 Buscando FORM_FIELD:', field.form_field.id, '- formDataSource:', formDataSource)

        if (formDataSource) {
          const formDataArray = formDataSource.data || formDataSource
          console.log('    formDataArray:', formDataArray)
          console.log('    formDataArray expandido:', JSON.parse(JSON.stringify(formDataArray)))
          if (Array.isArray(formDataArray)) {
            const customData = formDataArray.find(cd => cd.form_field_id === field.form_field.id)
            console.log('    Procurando form_field_id:', field.form_field.id)
            console.log('    customData encontrado:', customData)
            const fieldKey = field.form_field.name || ('field_' + field.form_field.id)
            if (customData) {
              console.log('  ✅ Campo FORM_FIELD carregado:', fieldKey, '=', customData.data)
              this.$set(this.customDataValues, fieldKey, customData.data)
            } else {
              // Registrar chave mesmo sem dados — necessário para visibility_rules
              // distinguir "campo existe mas vazio" vs "campo não existe no card"
              console.log('  ⚠️ Campo FORM_FIELD sem dados, registrando chave vazia:', fieldKey)
              if (!(fieldKey in this.customDataValues)) {
                this.$set(this.customDataValues, fieldKey, '')
              }
            }
          }
        } else {
          console.log('  ⚠️ formDataSource não existe')
        }
      }

      // Registration control field: carrega do registrationControlItem do participante
      if (field.field_source === 'registration_control' && field.field_config) {
        const fieldName = field.field_config.name || field.field_config.rc_field || ('rc_' + field.id)
        const rcField = field.field_config.rc_field

        console.log('  🔍 RC DEBUG - registration_control_item_id:', this.participant.registration_control_item_id)
        console.log('  🔍 RC DEBUG - participant.registrationControlItem:', this.participant.registrationControlItem)
        console.log('  🔍 RC DEBUG - registrationControlItemData computed:', this.registrationControlItemData)
        console.log('  🔍 RC DEBUG - displayRegistrationControl computed:', this.displayRegistrationControl)

        // registrationControlItem vem envolvido pelo Fractal em {data: {...}}
        var rcItemWrapper = this.participant.registrationControlItem
        var rcItem = rcItemWrapper && rcItemWrapper.data ? rcItemWrapper.data : rcItemWrapper

        console.log('  🔍 RC field:', rcField, '- rcItem:', rcItem)

        if (rcItem && rcField) {
          var value = null
          if (rcField === 'identifier') {
            value = rcItem.identifier
          } else if (rcField === 'name') {
            value = rcItem.name
          } else if (rcItem.data && rcItem.data[rcField] !== undefined) {
            value = rcItem.data[rcField]
          }

          console.log('  ✅ RC value for', rcField, '=', value)

          if (value !== null && value !== undefined) {
            this.$set(this.customDataValues, fieldName, value)
          }
        }
      }

      // Internal field: carrega de custom_data usando field_config.name
      if (field.field_source === 'internal') {
        const fieldName = (field.field_config && field.field_config.name) || field.id
        const formDataSource = (this.data && this.data.formData)
          ? this.data.formData
          : this.participant.formData

        console.log('  🔍 Buscando INTERNAL:', field.id, '- fieldName:', fieldName, '- formDataSource:', formDataSource)

        if (formDataSource) {
          const formDataArray = formDataSource.data || formDataSource
          console.log('    formDataArray:', formDataArray)
          console.log('    formDataArray expandido:', JSON.parse(JSON.stringify(formDataArray)))
          if (Array.isArray(formDataArray)) {
            // Internal fields são armazenados com card_field_id
            const customData = formDataArray.find(cd => cd.card_field_id === field.id)
            console.log('    Procurando card_field_id:', field.id)
            console.log('    customData encontrado:', customData)
            if (customData) {
              console.log('  ✅ Campo INTERNAL carregado:', fieldName, '=', customData.data)
              this.$set(this.customDataValues, fieldName, customData.data)
            } else {
              console.log('  ⚠️ Campo INTERNAL não encontrado no formData')
            }
          }
        } else {
          console.log('  ⚠️ formDataSource não existe')
        }
      }
    },
    getCardFieldKey (field) {
      if (field.field_source === 'standard') {
        return field.standard_field_name
      }
      if (field.field_source === 'form_field' && field.form_field) {
        return field.form_field.name || ('field_' + field.form_field.id)
      }
      if (field.field_source === 'internal') {
        return (field.field_config && field.field_config.name) || ('internal_' + field.id)
      }
      if (field.field_source === 'registration_control' && field.field_config) {
        return field.field_config.name || field.field_config.rc_field || ('rc_' + field.id)
      }
      return 'field_' + field.id
    },
    getCardFieldComponent (field) {
      // Standard fields
      if (field.field_source === 'standard') {
        const componentMap = {
          name: 'BaseInput',
          email: 'BaseInputEmail',
          cpf: 'BaseInputCPF',
          passaporte: 'BaseInput',
          phone: 'BaseInputPhone',
          gender: 'BaseSelect',
          birth_date: 'BaseInputDate',
          address: 'BaseInput',
          city: 'BaseInput',
          state: 'BaseSelect',
          zip_code: 'BaseInput',
          country: 'BaseInput'
        }
        return componentMap[field.standard_field_name] || 'BaseInput'
      }

      // Form field
      if (field.field_source === 'form_field' && field.form_field && field.form_field.field_type) {
        const componentName = field.form_field.field_type.component
        return this.mapComponentName(componentName || 'TextField')
      }

      // Internal field
      if (field.field_source === 'internal' && field.field_config) {
        const fieldType = field.field_config.field_type
        const componentMap = {
          text: 'BaseInput',
          textarea: 'BaseTextArea',
          number: 'BaseInput',
          select: 'BaseSelect',
          multiselect: 'BaseMultiselect',
          checkbox: 'BaseRadio',
          radio: 'BaseRadio',
          date: 'BaseInputDate',
          datetime: 'BaseInputDate',
          toggle: 'BaseRadio'
        }
        return componentMap[fieldType] || 'BaseInput'
      }

      // Registration control field
      if (field.field_source === 'registration_control' && field.field_config) {
        const fieldType = field.field_config.field_type
        const componentMap = {
          text: 'BaseInput',
          textarea: 'BaseTextArea',
          number: 'BaseInput',
          select: 'BaseSelect',
          multiselect: 'BaseMultiselect'
        }
        return componentMap[fieldType] || 'BaseInput'
      }

      return 'BaseInput'
    },
    isStateField (field) {
      var stateNames = ['state', 'ufs', 'uf', 'estado']

      if (field.field_source === 'standard') {
        return field.standard_field_name === 'state'
      }
      if (field.field_source === 'form_field' && field.form_field) {
        // 1) field_type dedicado (state/ufs)
        if (field.form_field.field_type && stateNames.indexOf(field.form_field.field_type.name) !== -1) {
          return true
        }
        // 2) field_type genérico (select) mas name convencional (estado/state/uf)
        var fieldName = (field.form_field.name || '').toLowerCase()
        if (stateNames.indexOf(fieldName) !== -1) {
          return true
        }
        return false
      }
      if (field.field_source === 'internal' && field.field_config) {
        if (stateNames.indexOf(field.field_config.field_type) !== -1) {
          return true
        }
        var internalName = (field.field_config.name || '').toLowerCase()
        return stateNames.indexOf(internalName) !== -1
      }
      return false
    },
    getCardFieldOptions (field) {
      // Campo de estado (UF): força lista canônica independente da origem
      // Motivo: dado salvo em participant_custom_data é sigla (BA, SP),
      // mas options do form_field podem estar com value=label ("BA - Bahia"),
      // o que impede o select de encontrar o valor salvo.
      if (this.isStateField(field)) {
        return this.states || []
      }

      // Standard fields com options
      if (field.field_source === 'standard') {
        if (field.standard_field_name === 'gender') {
          // Tentar carregar opções do formulário do grupo
          if (this.allFormFields && this.allFormFields.length > 0) {
            var genderField = this.allFormFields.find(function (f) {
              return f.field_type && f.field_type.maps_to_column === 'gender'
            })
            if (genderField && genderField.options && genderField.options.length > 0) {
              return genderField.options
            }
          }
          // Fallback padrão
          return [
            { value: 'Masculino', label: 'Masculino' },
            { value: 'Feminino', label: 'Feminino' },
            { value: 'Não-binário', label: 'Não-binário' },
            { value: 'Outro', label: 'Outro' },
            { value: 'Prefiro não informar', label: 'Prefiro não informar' }
          ]
        }
      }

      // Form field options
      if (field.field_source === 'form_field' && field.form_field) {
        return field.form_field.options || []
      }

      // Internal field options
      if (field.field_source === 'internal' && field.field_config && field.field_config.options) {
        return field.field_config.options
      }

      // Registration control field options
      if (field.field_source === 'registration_control' && field.field_config && field.field_config.options) {
        return field.field_config.options
      }

      return []
    },
    getCardFieldLabel (field) {
      // label_override tem prioridade (campos de acompanhante com label limpo)
      if (field.field_config && field.field_config.label_override) {
        return field.field_config.label_override
      }

      // Use label definido no card, ou fallback
      if (field.label) return field.label

      // Standard field label
      if (field.field_source === 'standard') {
        const labels = {
          name: 'Nome Completo',
          badge_name: 'Nome para Crachá',
          email: 'E-mail',
          cpf: 'CPF',
          passaporte: 'Passaporte',
          phone: 'Telefone',
          gender: 'Gênero',
          birth_date: 'Data de Nascimento',
          address: 'Endereço',
          city: 'Cidade',
          state: 'Estado',
          zip_code: 'CEP',
          country: 'País'
        }
        return labels[field.standard_field_name] || field.standard_field_name
      }

      // Form field label
      if (field.field_source === 'form_field' && field.form_field) {
        return field.form_field.label || field.form_field.name
      }

      // Internal field label
      if (field.field_source === 'internal' && field.field_config) {
        return field.field_config.label || field.field_config.name
      }

      // Registration control field label
      if (field.field_source === 'registration_control' && field.field_config) {
        return field.field_config.label || field.field_config.name
      }

      return 'Campo'
    },
    isAiValidated (fieldKey) {
      if (this.participant && Array.isArray(this.participant.ai_applied_fields) && this.participant.ai_applied_fields.includes(fieldKey)) return true
      return this.participant && this.participant.ai_validation_map && this.participant.ai_validation_map[fieldKey] === 'match'
    },
    hasAiMismatch (fieldKey) {
      return this.participant && this.participant.ai_validation_map && this.participant.ai_validation_map[fieldKey] === 'mismatch'
    },
    getAiFieldTooltip (fieldKey) {
      if (this.hasAiMismatch(fieldKey)) return 'Divergência detectada pela IA — revisar'
      if (this.isAiValidated(fieldKey)) return 'Validado via IA'
      return undefined
    },
    cardTabHasAiFields (tab) {
      if (!tab.fields || !tab.fields.length) return false
      return tab.fields.some(f => {
        const key = this.getCardFieldKey(f)
        return this.isAiValidated(key) || this.hasAiMismatch(key)
      })
    },
    stepHasAiFields (step) {
      if (!step.fields || !step.fields.length) return false
      return step.fields.some(f => this.isAiValidated(f.name) || this.hasAiMismatch(f.name))
    },
    getCardFieldSize (field) {
      // Usar cols ajustados se disponíveis (redistribuição proporcional)
      if (this.adjustedColsMap && this.adjustedColsMap[field.id]) {
        return this.adjustedColsMap[field.id]
      }
      if (field.layout && field.layout.cols) {
        return field.layout.cols
      }
      return 12
    },

    /**
     * Verifica se um campo deve ser exibido baseado em suas regras de visibilidade
     */
    isFieldVisible (field) {
      // Campo marcado como invisível
      if (field.is_visible === false) {
        return false
      }

      // Não tem regras de visibilidade - sempre visível
      if (!field.visibility_rules || field.visibility_rules.mode === 'all') {
        return true
      }

      const rules = field.visibility_rules

      // Modo: visível apenas para grupos específicos
      if (rules.mode === 'groups' && rules.groups && Array.isArray(rules.groups)) {
        const participantGroupId = this.participant.group_id
        return rules.groups.includes(participantGroupId)
      }

      // Modo: visibilidade condicional baseada em outros campos
      if (rules.mode === 'conditional' && rules.conditions && Array.isArray(rules.conditions)) {
        // Filtrar condições: ignorar condições que referenciam campos inexistentes no card.
        // Ex: "companion_1_has_companion" não existe no customDataValues do acompanhante
        // porque é um campo cross-context (do formulário, não do card).
        const relevantConditions = rules.conditions.filter(condition => {
          return condition.field in this.customDataValues
        })

        // Se nenhuma condição é relevante (todas cross-context), campo é visível
        if (relevantConditions.length === 0) {
          return true
        }

        // Avaliar apenas as condições relevantes (AND lógico)
        return relevantConditions.every(condition => {
          const fieldValue = this.customDataValues[condition.field]
          const conditionValue = condition.value

          switch (condition.operator) {
            case 'equals':
              return String(fieldValue || '') === String(conditionValue || '')
            case 'not_equals':
              return String(fieldValue || '') !== String(conditionValue || '')
            case 'contains':
              return fieldValue && String(fieldValue).includes(String(conditionValue))
            case 'not_contains':
              return !fieldValue || !String(fieldValue).includes(String(conditionValue))
            case 'not_empty':
              return fieldValue !== null && fieldValue !== undefined && fieldValue !== ''
            case 'empty':
              return fieldValue === null || fieldValue === undefined || fieldValue === ''
            default:
              return true
          }
        })
      }

      return true
    },

    /**
     * Aba veio de um formulário de ação de retorno (follow-up)?
     * Essas abas são preenchidas pelo participante na LP/widget; o backend só as
     * exibe quando há uma pending action elegível (source_form_id setado na tab).
     */
    isFollowUpTab (tab) {
      return !!(tab && tab.source_form_id)
    },

    /**
     * Algum campo da aba já tem valor preenchido?
     */
    tabHasFilledValue (tab) {
      if (!tab || !tab.fields) {
        return false
      }
      return tab.fields.some(field => {
        const value = this.customDataValues[this.getCardFieldKey(field)]
        return value !== null && value !== undefined && value !== ''
      })
    },

    /**
     * Os campos obrigatórios desta aba devem travar o save?
     * - Abas de cadastro: sempre (comportamento normal).
     * - Abas de ação de retorno: só quando já houver algum valor preenchido.
     *   Em branco (participante ainda não respondeu) NÃO bloqueia o operador,
     *   que precisa poder editar e-mail/outros dados de cadastro à parte.
     */
    shouldEnforceRequired (tab) {
      if (!this.isFollowUpTab(tab)) {
        return true
      }
      return this.tabHasFilledValue(tab)
    },

    /**
     * Valida um campo específico e marca erro se necessário
     */
    validateField (field, enforceRequired = true) {
      const fieldKey = this.getCardFieldKey(field)
      const value = this.customDataValues[fieldKey]
      const isVisible = this.isFieldVisible(field)

      // Campo invisível não precisa validação
      if (!isVisible) {
        this.$set(this.fieldErrors, fieldKey, false)
        return true
      }

      // Validar se campo obrigatório está preenchido
      // (enforceRequired=false para abas de ação de retorno ainda em branco)
      if (field.is_required && enforceRequired) {
        const isEmpty = value === null || value === undefined || value === ''
        this.$set(this.fieldErrors, fieldKey, isEmpty)
        return !isEmpty
      }

      // Campo não obrigatório (ou não exigido neste contexto) - sempre válido
      this.$set(this.fieldErrors, fieldKey, false)
      return true
    },

    /**
     * Valida todos os campos do participantCard
     * Retorna true se todos válidos, false se algum inválido
     */
    validateAllFields () {
      if (!this.participantCard || !this.participantCard.tabs) {
        return true
      }

      let isValid = true
      const invalidFields = []

      this.participantCard.tabs.forEach(tab => {
        const enforceRequired = this.shouldEnforceRequired(tab)
        if (tab.fields) {
          tab.fields.forEach(field => {
            const fieldValid = this.validateField(field, enforceRequired)
            if (!fieldValid) {
              isValid = false
              const fieldLabel = this.getCardFieldLabel(field)
              invalidFields.push(fieldLabel)
            }
          })
        }
      })

      // Forçar re-render dos componentes com erros
      this.forceValidation = true
      this.$nextTick(() => {
        this.forceValidation = false
      })

      if (!isValid) {
        this.hasTriedSubmit = true // Marcar que tentou fazer submit
        errorMessage('Preencha todos os campos obrigatórios: ' + invalidFields.join(', '))
        window.scroll(0, 0)
      } else {
        this.hasTriedSubmit = false // Limpar flag se validação passou
      }

      return isValid
    },

    /**
     * Verifica se uma aba tem campos com erros
     * Só exibe badge se já tentou fazer submit
     */
    tabHasErrors (tab) {
      if (!this.hasTriedSubmit) {
        return false
      }
      return this.countInvalidFieldsInTab(tab) > 0
    },

    /**
     * Conta quantos campos obrigatórios NÃO preenchidos existem em uma aba
     */
    countInvalidFieldsInTab (tab) {
      if (!tab || !tab.fields) {
        return 0
      }

      // Aba de ação de retorno ainda em branco não conta como pendência
      // (não bloqueia o save, então não deve mostrar badge de erro).
      if (!this.shouldEnforceRequired(tab)) {
        return 0
      }

      let count = 0
      tab.fields.forEach(field => {
        const isVisible = this.isFieldVisible(field)
        const isRequired = field.is_required
        const fieldKey = this.getCardFieldKey(field)
        const value = this.customDataValues[fieldKey]
        const isEmpty = value === null || value === undefined || value === ''

        if (isVisible && isRequired && isEmpty) {
          count++
        }
      })

      return count
    },

    updateCustomDataValue (fieldName, value) {
      this.$set(this.customDataValues, fieldName, value)

      // Limpar erro do campo quando valor é alterado
      if (this.fieldErrors[fieldName]) {
        this.$set(this.fieldErrors, fieldName, false)
      }
    },
    getRegistrationControlFieldLabel (key) {
      var itemData = this.registrationControlItemData
      if (itemData.fields_schema && Array.isArray(itemData.fields_schema)) {
        var field = itemData.fields_schema.find(function (f) { return f.name === key })
        if (field && field.label) {
          return field.label
        }
      }
      return key
    },
    mapComponentName (componentName) {
      const componentMap = {
        // Text / Input
        'TextField': 'BaseInput',
        'NumberField': 'BaseInput',
        'UrlField': 'BaseInput',
        'HiddenField': 'BaseInput',
        'PasswordField': 'BaseInput',
        'CurrencyBrlField': 'BaseInput',
        'DecimalField': 'BaseInput',
        'PercentageField': 'BaseInput',
        'RgField': 'BaseInput',
        'CepField': 'BaseInput',
        'AddressField': 'BaseInput',
        'CityField': 'BaseInput',
        // Textarea
        'TextareaField': 'BaseTextArea',
        'TextAreaField': 'BaseTextArea',
        // Email
        'EmailField': 'BaseInputEmail',
        // Phone
        'PhoneField': 'BaseInputPhone',
        // CPF / CNPJ
        'CpfField': 'BaseInputCPF',
        'CPFField': 'BaseInputCPF',
        'CnpjField': 'BaseInputCNPJ',
        'CNPJField': 'BaseInputCNPJ',
        'CpfCnpjField': 'BaseInputCPF',
        // Select / Dropdown
        'SelectField': 'BaseSelect',
        'MultiselectField': 'BaseMultiselect',
        'AutocompleteField': 'BaseSelect',
        'StateField': 'BaseSelect',
        // Radio / Checkbox / Toggle
        'RadioField': 'BaseRadio',
        'CheckboxField': 'BaseRadio',
        'ToggleField': 'BaseRadio',
        'CheckboxSingleField': 'BaseRadio',
        // Termos de aceite (readonly) — mostra checkbox marcado/desmarcado com status
        'TermsField': 'BaseTermsCheckbox',
        'TermsAcceptanceField': 'BaseTermsCheckbox',
        // Date / Time
        'DateField': 'BaseInputDate',
        'TimeField': 'BaseInputDate',
        'DatetimeField': 'BaseInputDateTime',
        'DateRangeField': 'BaseInputDate',
        'BirthdateField': 'BaseInputDate',
        // Special (fallback to BaseInput)
        'FileField': 'BaseInput',
        'ImageField': 'BaseInput',
        'SignatureField': 'BaseInput',
        'FacialField': 'BaseInput',
        'RatingField': 'BaseInput',
        'SliderField': 'BaseInput',
        'ColorField': 'BaseInput'
      }
      return componentMap[componentName] || 'BaseInput'
    },
    getFieldSize (field) {
      if (field.layout && field.layout.cols) {
        return parseInt(field.layout.cols)
      }

      const fieldType = field.field_type ? field.field_type.name : 'text'
      const label = field.label ? field.label.toLowerCase() : ''

      if (label.includes('nome completo') || label.includes('endereço') || label.includes('observ')) {
        return 12
      }
      if (fieldType === 'email' || fieldType === 'phone' || fieldType === 'cpf' || fieldType === 'cnpj' || fieldType === 'passaporte') {
        return 6
      }
      if (fieldType === 'date' || fieldType === 'number') {
        return 4
      }
      if (fieldType === 'select' || fieldType === 'radio') {
        return 6
      }
      if (fieldType === 'textarea') {
        return 12
      }

      return 6
    },
    newCompanion (participantId) {
      this.$refs.formCompanion.open(participantId)
      this.modalForm = true
    },
    openCompanion (participantId) {
      // Emite evento global para abrir o Form.vue completo (com participant card)
      // O Index.vue captura e abre uma nova instância do Form principal
      this.$root.$emit('open-participant', participantId)
    },
    openLinkCompanion () {
      this.$refs.linkCompanion.open()
    },
    async unlinkCompanionAction (companion) {
      if (!(this.isUserAdmin || this.isUserMaster)) {
        errorMessage('Atenção: Você não tem permissão para realizar essa ação!', 2500)
        return
      }
      const r = await Swal.fire({
        title: 'Desvincular acompanhante?',
        html: `<b>${companion.name}</b> voltará a ser uma inscrição independente.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f46a6a',
        cancelButtonColor: '#4a4848',
        cancelButtonText: 'CANCELAR',
        confirmButtonText: 'SIM, DESVINCULAR'
      })
      if (!r.value) return
      try {
        await apiUnlinkCompanion(companion.id)
        successMessage('Acompanhante desvinculado com sucesso.', 2500)
        this.loadItem(this.idParticipant)
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Falha ao desvincular acompanhante.'
        errorMessage('Alerta: ' + msg)
      }
    },
    open (id = null) {
      this.modal = true
      this.loading = true
      if (id) {
        this.loadItem(id)
        return
      }
      this.loading = false
    },
    deleteParticipant (participant) {
      if (this.isUserAdmin) {
        Swal.fire({
          title: 'Você tem certeza ?',
          html: `Será excluído do sistema <b>${participant.name}</b>`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#f46a6a',
          cancelButtonColor: '#4a4848',
          cancelButtonText: 'CANCELAR',
          confirmButtonText: 'SIM, DESEJO EXCLUIR!'
        }).then(result => {
          if (result.value) {
            try {
              this.destroyParticipant(participant.id).then((res) => {
                successMessage('Sucesso: Participante excluído com sucesso.', 2500)
                this.close()
              }).catch((err) => {
                errorMessage('Alerta: ' + err.message)
              })
            } catch (err) {
              errorMessage('Alerta: ' + err.response.data.message)
            }
          }
        })
        return
      }
      errorMessage('Atenção: Você não tem permissão para realizar essa ação!', 2500)
    },
    toggleGroup (participantId, groupId, newGroup, oldGroup) {
      if (this.isUserAdmin || this.isUserMaster) {
        Swal.fire({
          title: 'Você tem certeza?',
          html: `Mudar grupo de <b>${oldGroup}</b> para <b>${newGroup}</b> <br /><small>Dados podem ser perdidos.</small>`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#388741',
          cancelButtonColor: '#4a4848',
          cancelButtonText: 'CANCELAR',
          confirmButtonText: 'SIM, MUDAR GRUPO'
        }).then(result => {
          if (result.value) {
            try {
              this.updateType({ id: participantId, group_id: groupId }).then((res) => {
                successMessage('Sucesso: Grupo atualizado.', 2500)
              }).then(() => {
                this.loadItem(participantId)
              }).catch((err) => {
                errorMessage('Alerta: ' + err.message)
              })
            } catch (err) {
              console.log(err)
            }
          }
        })
        return
      }
      errorMessage('Atenção: Você não tem permissão para realizar essa ação!', 2500)
    },
    async toggleStatus (participantId, status) {
      if (this.isUserAdmin || this.isUserMaster) {
        if (status === 2) {
          Swal.fire({
            title: 'Você tem certeza?',
            input: 'select',
            html: `Mudar status de <b>${this.statusList[this.participant.status]}</b> para <b>${this.statusList[status]}</b>`,
            inputOptions: this.refstatusList,
            icon: 'question',
            inputPlaceholder: 'Selecione motivo da pendência',
            showCancelButton: true,
            confirmButtonColor: '#388741',
            cancelButtonColor: '#4a4848',
            cancelButtonText: 'CANCELAR',
            confirmButtonText: 'SIM, MUDAR STATUS',
            inputValidator: (value) => {
              return new Promise((resolve) => {
                if (value) {
                  resolve()
                } else {
                  resolve('Você precisa seleciona o motivo')
                }
              })
            }
          }).then(result => {
            if (result.value) {
              try {
                this.updateStatus({ id: participantId, status, refSatus: result.value }).then((res) => {
                  successMessage('Sucesso: Status atualizado.', 2500)
                  // AUTO-ATRIBUIR quando status = 1 (Em Andamento)
                  if (status === 1) {
                    services.autoAssignOnStatusChange(participantId).then(() => {
                      console.log('Ficha atribuída automaticamente')
                    }).catch(err => {
                      console.log('Erro ao auto-atribuir:', err)
                    })
                  }
                  if (status === 3) {
                    EventBus.$emit('participantConfirmed', true)
                  }
                }).then(() => {
                  this.loadItem(participantId)
                }).catch((err) => {
                  errorMessage('Alerta: ' + err.message)
                })
              } catch (err) {
                console.log(err)
              }
            }
          })
        } else {
          Swal.fire({
            title: 'Você tem certeza?',
            html: `Mudar status de <b>${this.statusList[this.participant.status]}</b> para <b>${this.statusList[status]}</b>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#388741',
            cancelButtonColor: '#4a4848',
            cancelButtonText: 'CANCELAR',
            confirmButtonText: 'SIM, MUDAR STATUS'
          }).then(result => {
            if (result.value) {
              try {
                this.updateStatus({ id: participantId, status }).then((res) => {
                  successMessage('Sucesso: Status atualizado.', 2500)
                  // AUTO-ATRIBUIR quando status = 1 (Em Andamento)
                  if (status === 1) {
                    services.autoAssignOnStatusChange(participantId).then(() => {
                      console.log('Ficha atribuída automaticamente ao atendente')
                    }).catch(err => {
                      console.log('Erro ao auto-atribuir:', err)
                    })
                  }
                }).then(() => {
                  this.loadItem(participantId)
                }).catch((err) => {
                  errorMessage('Alerta: ' + err.message)
                })
              } catch (err) {
                console.log(err)
              }
            }
          })
        }
        return
      }
      errorMessage('Atenção: Você não tem permissão para realizar essa ação!', 2500)
    },
    getGroups () {
      let params = {
        search: null,
        limit: 100,
        page: 1
      }
      this.params = params
      this.getAll(this.params).then((res) => {
      }).catch(error => {
        console.log(error)
      })
    },
    getTags (filter) {
      let params = {
        search: this.search || null,
        limit: 100,
        page: 1
      }
      this.params = params
      this.getAllTags(this.params).then((res) => {
      }).catch(error => {
        console.log(error)
      })
    },
    async saveParticipant () {
      console.log('🚀 [DEBUG] saveParticipant iniciado')
      console.log('🚀 [DEBUG] participantCard:', this.participantCard)
      console.log('🚀 [DEBUG] customDataValues:', this.customDataValues)

      // IMPORTANTE: Copiar campos standard de customDataValues para participant ANTES da validação
      if (this.participantCard && this.participantCard.tabs) {
        console.log('📋 [DEBUG] Copiando campos standard para participant antes da validação')
        var self = this
        this.participantCard.tabs.forEach(function (tab) {
          if (tab.fields) {
            tab.fields.forEach(function (field) {
              if (field.field_source === 'standard' && field.standard_field_name) {
                var fieldKey = field.standard_field_name
                var value = self.customDataValues[fieldKey]

                if (value !== undefined && value !== null && value !== '') {
                  var columnMap = {
                    name: 'name',
                    badge_name: 'badge_name',
                    email: 'email',
                    cpf: 'cpf',
                    passaporte: 'passaporte',
                    phone: 'phone',
                    gender: 'gender',
                    birth_date: 'birth_date',
                    address: 'address',
                    city: 'city',
                    state: 'state',
                    zip_code: 'zip_code',
                    country: 'country'
                  }
                  var columnName = columnMap[field.standard_field_name]
                  if (columnName) {
                    // Converter data de DD/MM/YYYY para YYYY-MM-DD antes de salvar
                    if (field.standard_field_name === 'birth_date' && value) {
                      if (value.includes('/')) {
                        var parts = value.split('/')
                        if (parts.length === 3) {
                          value = parts[2] + '-' + parts[1] + '-' + parts[0]
                        }
                      } else if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
                        value = value.substring(0, 10)
                      }
                    }
                    console.log('  → Pré-validação: copiando', columnName, '=', value)
                    self.participant[columnName] = value
                  }
                }
              }
            })
          }
        })
      }

      console.log('🔍 [DEBUG] participant após cópia:', this.participant)

      // Validação: pula Vuelidate se há participantCard (campos têm validação própria)
      if (this.participantCard) {
        console.log('✅ [DEBUG] participantCard detectado - usando validateAllFields()')

        // Validar todos os campos e marcar erros visualmente
        if (!this.validateAllFields()) {
          console.log('❌ [DEBUG] Validação falhou - há campos obrigatórios não preenchidos')
          return
        }

        console.log('✅ [DEBUG] Todos os campos obrigatórios estão preenchidos')
      } else {
        console.log('⚠️ [DEBUG] Usando validação Vuelidate (sistema antigo)')
        this.submitted = true
        this.$v.$touch()

        console.log('🚀 [DEBUG] Validação - $v.$invalid:', this.$v.$invalid)

        if (this.$v.$invalid) {
          console.log('❌ [DEBUG] Validação falhou, abortando')
          console.log('❌ [DEBUG] Campos inválidos:')
          if (this.$v.participant) {
            Object.keys(this.$v.participant).forEach(function (key) {
              if (key.indexOf('$') !== 0 && this.$v.participant[key].$invalid) {
                console.log('  - ' + key + ':', this.$v.participant[key])
                console.log('    Valor atual:', this.participant[key])
              }
            }.bind(this))
          }
          window.scroll(0, 0)
          return
        }
      }

      console.log('✅ [DEBUG] Validação passou, continuando...')
      this.loading = true
      this.participant.transports = []
      if (this.idaTransports && this.idaTransports.length > 0) {
        this.participant.transports = [...this.participant.transports, ...this.idaTransports]
      }
      if (this.voltaTransports && this.voltaTransports.length > 0) {
        this.participant.transports = [...this.participant.transports, ...this.voltaTransports]
      }
      if (this.participant.transports.length) {
        this.participant.transports.forEach(transport => {
          transport.boarding_date = transport.boarding_date ? this.$moment(transport.boarding_date).format('YYYY-MM-DD HH:mm:ss') : null
          transport.landing_date = transport.landing_date ? this.$moment(transport.landing_date).format('YYYY-MM-DD HH:mm:ss') : null
        })
      }
      this.participant.contact = this.contact

      console.log('📋 [DEBUG] Iniciando processamento de campos')

      // Inicializar customData
      this.participant.customData = []

      // NOVO SISTEMA: Processar campos custom do participantCard (standard já foram copiados antes da validação)
      console.log('🔍 [DEBUG] Verificando participantCard:', !!this.participantCard)
      if (this.participantCard && this.participantCard.tabs) {
        console.log('✅ [DEBUG] Processando campos CUSTOM do participantCard')
        console.log('✅ [DEBUG] Total de abas:', this.participantCard.tabs.length)
        this.participantCard.tabs.forEach(function (tab, tabIdx) {
          console.log('📑 [DEBUG] Processando aba #' + tabIdx + ':', tab.name, '- Total de campos:', tab.fields ? tab.fields.length : 0)
          if (tab.fields) {
            tab.fields.forEach(function (field, fieldIdx) {
              var fieldKey = self.getCardFieldKey(field)
              var value = self.customDataValues[fieldKey]

              console.log('🔍 [DEBUG] Campo #' + fieldIdx + ':', {
                label: field.label || (field.field_config && field.field_config.label),
                source: field.field_source,
                key: fieldKey,
                value: value,
                hasValue: value !== undefined && value !== null && value !== ''
              })

              if (value !== undefined && value !== null && value !== '') {
                console.log('✅ [DEBUG] Campo tem valor, processando...')

                // Form field -> adiciona em customData com form_field_id
                if (field.field_source === 'form_field' && field.form_field) {
                  console.log('📝 [DEBUG] Salvando FORM_FIELD em customData:', field.form_field.id)
                  self.participant.customData.push({
                    form_field_id: field.form_field.id,
                    data: value
                  })
                }

                // Internal field -> adiciona em customData com card_field_id
                if (field.field_source === 'internal') {
                  console.log('📝 [DEBUG] Salvando INTERNAL em customData:', field.id)
                  self.participant.customData.push({
                    card_field_id: field.id,
                    field_name: (field.field_config && field.field_config.name) || ('internal_' + field.id),
                    data: value
                  })
                }

                // Registration control field -> adiciona em customData com card_field_id
                if (field.field_source === 'registration_control') {
                  console.log('📝 [DEBUG] Salvando REGISTRATION_CONTROL em customData:', field.id)
                  self.participant.customData.push({
                    card_field_id: field.id,
                    field_name: (field.field_config && field.field_config.name) || ('rc_' + field.id),
                    data: value
                  })
                }

                // Campos standard já foram processados antes da validação
                if (field.field_source === 'standard') {
                  console.log('⏭️  [DEBUG] Campo STANDARD já foi copiado antes da validação')
                }
              } else {
                console.log('⏭️  [DEBUG] Campo vazio ou undefined, pulando')
              }
            })
          }
        })
      }

      // SISTEMA ANTIGO (fallback): Processar campos do formSchema
      if (!this.participantCard && this.formSchema && this.allFormFields.length > 0) {
        console.log('⚠️  [DEBUG] Usando sistema ANTIGO (formSchema) - participantCard não encontrado')
        console.log('⚠️  [DEBUG] Total de campos no formSchema:', this.allFormFields.length)
        this.allFormFields.forEach(field => {
          const value = this.customDataValues[field.name]

          if (value !== undefined && value !== null && value !== '') {
            // Verifica se é campo standard
            const isStandard = field.field_type && field.field_type.is_standard && field.field_type.maps_to_column

            if (isStandard) {
              // Campo standard -> salva direto no participant
              const columnName = field.field_type.maps_to_column
              this.participant[columnName] = value
            } else {
              // Campo custom -> adiciona em customData
              this.participant.customData.push({
                form_id: this.formSchema.id,
                form_field_id: field.id,
                data: value
              })
            }
          }
        })
      }

      console.log('💾 [DEBUG] Dados coletados - participant:', this.participant)
      console.log('💾 [DEBUG] customData final:', this.participant.customData)
      console.log('💾 [DEBUG] Chamando postData...')

      await this.postData(this.participant).then(res => {
        console.log('✅ [DEBUG] postData sucesso:', res)
        let message = 'Sucesso: Dados armazenados com sucesso.'
        let delay = 2500
        successMessage(message, delay)
        this.close()
      }).catch((err) => {
        console.log('❌ [DEBUG] postData erro:', err)
        errorMessage('Alerta: ' + err.message)
        this.loading = false
      })
    },
    close () {
      if (this.modal) {
        this.participant = {}
        this.customDataValues = {}
        this.participantCard = null
        this.formSchema = null
        this.transports = []
        this.contact = {}
        this.companion = {}
        this.attachments = []
        this.idaTransports = []
        this.voltaTransports = []
        this.$v.$reset()
        this.hasTriedSubmit = false
        this.fieldErrors = {}
        this.$emit('created')
        this.modal = false
      }
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('tag', ['tags', 'pagination']),
    ...mapGetters('settings', { statusList: 'STATUSLIST' }),
    ...mapGetters('settings', { refstatusList: 'REFSTATUSLIST' }),
    ...mapState('settingRSVP', { settingRSVP: 'settingRSVP' }),
    ...mapState('participant', { data: 'participant' }),
    ...mapState('group', ['groups']),
    /**
     * Redistribui proporcionalmente os cols dos campos visiveis em cada row de 12 colunas.
     * Quando campos sao omitidos pelo backend (display_for), os restantes expandem para preencher a linha.
     */
    adjustedColsMap () {
      if (!this.participantCard || !this.participantCard.tabs) return {}
      const map = {}
      this.participantCard.tabs.forEach(tab => {
        if (!tab.fields) return
        const visibleFields = tab.fields.filter(f => this.isFieldVisible(f))
        // Agrupar campos em rows de 12 colunas
        let rowFields = []
        let rowTotal = 0
        const rows = []
        visibleFields.forEach(f => {
          const cols = (f.layout && f.layout.cols) ? parseInt(f.layout.cols) : 12
          if (rowTotal + cols > 12 && rowFields.length > 0) {
            rows.push({ fields: rowFields, total: rowTotal })
            rowFields = []
            rowTotal = 0
          }
          rowFields.push({ field: f, cols })
          rowTotal += cols
        })
        if (rowFields.length > 0) {
          rows.push({ fields: rowFields, total: rowTotal })
        }
        // Redistribuir cols em rows incompletas (apenas quando há múltiplos campos)
        rows.forEach(row => {
          if (row.total < 12 && row.total > 0 && row.fields.length > 1) {
            const n = row.fields.length
            const base = Math.floor(12 / n)
            let remainder = 12 - (base * n)
            row.fields.forEach(item => {
              // Divide 12 igualmente entre os campos da row
              map[item.field.id] = base + (remainder > 0 ? 1 : 0)
              if (remainder > 0) remainder--
            })
          }
        })
      })
      return map
    },
    aiAnalysisBanner () {
      const map = this.participant && this.participant.ai_validation_map
      const docAlerts = (this.participant && this.participant.ai_document_alerts) || []

      const hasMap = map && typeof map === 'object' && Object.keys(map).length > 0
      const hasAlerts = docAlerts.length > 0

      if (!hasMap && !hasAlerts) return null

      // Contagem de campos
      const entries = hasMap ? Object.entries(map) : []
      const verifiedFlightCount = this.participant?.ai_verified_flight_field_count || 0
      const countFor = (key) => key === 'flights' ? verifiedFlightCount : 1
      const matchCount = entries.filter(([, s]) => s === 'match').reduce((sum, [k]) => sum + countFor(k), 0)
      const mismatchCount = entries.filter(([, s]) => s === 'mismatch').reduce((sum, [k]) => sum + countFor(k), 0)
      const total = matchCount + mismatchCount

      // Classificar alertas
      const hasCritical = docAlerts.some(a => a.severity === 'critical')
      const hasWarningAlerts = docAlerts.some(a => a.severity === 'warning')

      // Montar subtitle de campos
      let subtitle = ''
      if (total > 0) {
        if (mismatchCount > 0) {
          subtitle = `${total} campos verificados · ${mismatchCount} ${mismatchCount === 1 ? 'divergência encontrada' : 'divergências encontradas'}`
        } else {
          subtitle = `${total} ${total === 1 ? 'campo verificado' : 'campos verificados'} — todos os dados conferem`
        }
      }

      // Determinar tipo do banner (prioridade: critical > warning/mismatch > valid)
      let type = 'ai-banner--valid'
      let title = 'Ficha validada por inteligência artificial'

      if (hasCritical) {
        type = 'ai-banner--danger'
        title = 'Análise de IA concluída'
      } else if (hasWarningAlerts || mismatchCount > 0) {
        type = 'ai-banner--warning'
        title = 'Análise de IA concluída'
      }

      return { type, title, subtitle, alerts: docAlerts }
    },
    visibleCardTabs () {
      if (!this.participantCard || !this.participantCard.tabs) {
        return []
      }
      return this.participantCard.tabs.filter(function (tab) {
        return tab.fields && tab.fields.length > 0
      })
    },
    allFormFields () {
      if (!this.formSchema) return []

      let fields = []

      // Se tem steps, coleta campos de todos os steps (exceto companion steps)
      if (this.formSchema.steps && this.formSchema.steps.length > 0) {
        this.formSchema.steps.forEach(step => {
          if (step.is_companion_step) return
          if (step.fields && step.fields.length > 0) {
            fields = fields.concat(step.fields)
          }
        })
      }

      // Adiciona campos root (sem step)
      if (this.formSchema.fields && this.formSchema.fields.length > 0) {
        fields = fields.concat(this.formSchema.fields)
      }

      return fields
    },
    hasCustomFields () {
      return this.allFormFields.length > 0
    },
    displayAereo () {
      // Controla exibição do módulo de logística de transporte
      return this.settingRSVP && this.settingRSVP.has_logistics
    },
    displayRegistrationControl () {
      return this.settingRSVP &&
        this.settingRSVP.registration_control_enabled &&
        this.participant.registrationControlItem &&
        this.participant.registrationControlItem.data
    },
    registrationControlItemData () {
      if (this.participant.registrationControlItem && this.participant.registrationControlItem.data) {
        return this.participant.registrationControlItem.data
      }
      return {}
    },
    displayCompanion () {
      if (parseInt(this.settingRSVP.registration_type) === 2) {
        return true
      }
      return false
    },
    maxCompanions () {
      const cfg = this.participant && this.participant.form && this.participant.form.companion_config
      const max = cfg && cfg.max_companions
      return Number.isFinite(parseInt(max)) ? parseInt(max) : 2
    },
    displayFinancialBlock () {
      const enabled = !!(this.settingRSVP && this.settingRSVP.has_financial_control)
      if (!enabled || !this.idParticipant) return false
      const isSuper = this.$store.getters['auth/isSuperAdmin']
      const has = this.$store.getters['auth/hasPermission']
      return isSuper || (typeof has === 'function' ? has('financial_control.view') : false)
    },
    isUserAdmin () {
      return this.$store.getters['auth/isAdmin']
    },
    isUserMaster () {
      return this.$store.getters['auth/isMaster']
    },
    eventName () {
      return process.env.VUE_APP_EVENT_NAME
    }
  },
  created () {
    this._loadingItem = false
  },
  mounted () {
    this.getGroups()
    this.GetRSVP()
    this.getTags()
    this.loadJourneyConfig()
    this._reloadHandler = (targetId) => {
      if (this.idParticipant && (!targetId || String(targetId) === String(this.idParticipant))) {
        this.loadItem(this.idParticipant)
      }
    }
    this.$root.$on('participant:reload', this._reloadHandler)
  },
  beforeDestroy () {
    if (this._reloadHandler) {
      this.$root.$off('participant:reload', this._reloadHandler)
    }
    this.stopAiPolling()
  },
  watch: {
    modal () {
      if (!this.modal) {
        this.close()
      }
    },
    'participant.state' (value) {
      this.selected = this.states.filter(o => o.value === value)[0]
    },
    formSchema: {
      handler (newVal) {
        if (newVal) {
          console.log('🔍 Detecção de Módulos de Transporte e Emergência')
        }
      },
      immediate: true
    },
    customDataValues: {
      handler (newVal) {
        if (!newVal) return

        // Auto-preencher origem do primeiro voo de IDA
        if (newVal['origem']) {
          console.log('✈️ Preenchendo ORIGEM do voo de IDA:', newVal['origem'])
          if (this.idaTransports.length === 0) {
            this.addVoo('IDA')
          }
          if (this.idaTransports[0]) {
            this.$set(this.idaTransports[0], 'origin', newVal['origem'])
          }
        }

        // Auto-preencher origem do primeiro voo de VOLTA
        if (newVal['retorno']) {
          console.log('🛬 Preenchendo ORIGEM do voo de VOLTA:', newVal['retorno'])
          if (this.voltaTransports.length === 0) {
            this.addVoo('VOLTA')
          }
          if (this.voltaTransports[0]) {
            this.$set(this.voltaTransports[0], 'origin', newVal['retorno'])
          }
        }
      },
      deep: true,
      immediate: true
    }
  }
}
</script>
<style scoped>
.vld-overlay.is-full-page {
    z-index: 1051 !important;
}
.modal-header {
  background: #000 !important;
  color: #fff;
}
.border-gray {
  border: 1px solid #e9e9e9;
}
.form-control:disabled, .form-control[readonly] {
  background-color: #f3f3f3 !important;
  opacity: 1;
}
>>> .form-control:disabled, >>> .form-control[readonly] {
  background-color: #e9ecef !important;
  opacity: 1;
  cursor: default;
}

/* Chevron Icon Animation */
div[role="tablist"] > .card.shadow-none {
  border-radius: 0.25rem !important;
  overflow: hidden;
}

/* Allow multiselect dropdown to appear outside the tags card boundary */
div[role="tablist"] > .card.shadow-none.tags-card {
  overflow: visible;
}

.chevron-icon {
  font-size: 18px;
  transition: transform 0.2s ease;
  color: #6c757d;
  cursor: pointer;
}

.chevron-icon.collapsed {
  transform: rotate(-90deg);
}

.badge-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  font-size: 9px;
  line-height: 1;
  border-radius: 3px;
}

/* ── AI Analysis Banner ── */
.ai-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 0.25rem;
  margin-bottom: 16px;
  border: 1px solid;
  position: relative;
  overflow: hidden;
}

.ai-banner--clickable {
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.15s;
}

.ai-banner--clickable:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}

.ai-banner--clickable:active {
  transform: translateY(0);
}

.ai-banner::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0.04;
  background: linear-gradient(135deg, currentColor 0%, transparent 60%);
  pointer-events: none;
}

.ai-banner--valid {
  background: linear-gradient(135deg, #f0fdf4 0%, #f8fafc 100%);
  border-color: #bbf7d0;
  color: #15803d;
}

.ai-banner--warning {
  background: linear-gradient(135deg, #fffbeb 0%, #f8fafc 100%);
  border-color: #fde68a;
  color: #b45309;
}

.ai-banner-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 0.25rem;
  background: currentColor;
  color: #fff;
}

.ai-banner--valid .ai-banner-icon {
  background: linear-gradient(135deg, #22c55e, #16a34a);
}

.ai-banner--warning .ai-banner-icon {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.ai-banner-icon svg {
  color: #fff;
}

.ai-banner-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.ai-banner-title {
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.3;
}

.ai-banner-subtitle {
  font-size: 0.75rem;
  font-weight: 400;
  opacity: 0.75;
  line-height: 1.3;
}

.ai-banner-badge {
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 3px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  white-space: nowrap;
}

.ai-banner--valid .ai-banner-badge {
  background: rgba(34, 197, 94, 0.12);
  color: #15803d;
}

.ai-banner--warning .ai-banner-badge {
  background: rgba(245, 158, 11, 0.12);
  color: #b45309;
}

/* ── Danger (critical alerts) ── */
.ai-banner--danger {
  background: linear-gradient(135deg, #fef2f2 0%, #f8fafc 100%);
  border-color: #fecaca;
  color: #b91c1c;
}

.ai-banner--danger .ai-banner-icon {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.ai-banner--danger .ai-banner-badge {
  background: rgba(239, 68, 68, 0.12);
  color: #b91c1c;
}

/* ── Alert lines inside banner ── */
.ai-banner-alert {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.4;
}

.ai-banner-alert i {
  font-size: 14px;
  flex-shrink: 0;
}

.ai-banner-alert--critical {
  color: #dc2626;
}

.ai-banner-alert--warning {
  color: #d97706;
}

/* ── AI Validated Field (deep para alcançar label dentro de componentes filhos) ── */
.ai-validated ::v-deep .label::after,
.ai-validated ::v-deep label::after {
  content: "\ecb9";
  font-family: "boxicons" !important;
  font-size: 12px;
  color: #34c38f;
  margin-left: 4px;
  vertical-align: middle;
}

/* ── AI Mismatch Field ── */
.ai-mismatch ::v-deep .label::after,
.ai-mismatch ::v-deep label::after {
  content: "\eb02";
  font-family: "boxicons" !important;
  font-size: 12px;
  color: #f46a6a;
  margin-left: 4px;
  vertical-align: middle;
}

</style>
